import { useCallback, useEffect, useRef, useState } from "react";
import { createConsumer } from "@rails/actioncable";
import type { Consumer, Subscription } from "@rails/actioncable";
import type {
  AnalysisCableMessage,
  AnalysisResult,
  AnalyzedLocation,
  LocationSuggestion,
} from "../lib/types";
import { analyseLocation } from "../lib/api";
import { AnalysisForm } from "../components/analysis-form";
import { AnalysisResultView } from "../components/analysis-result";
import { QuotaExhausted } from "../components/quota-exhausted";

interface AnalysePageProps {
  /**
   * Set on the /emplacement/:slug deep-link route. The server already
   * resolved + validated the Location, so we skip the autocomplete and
   * auto-fire the analysis on mount.
   */
  preselectedLocation?: LocationSuggestion;
  /**
   * Soft server-side error from the slug resolution (e.g. the Location
   * isn't geolocated). Surfaced inline above the form.
   */
  preselectError?: string;
  /**
   * Server-side pre-flight: no published `qr_code` agent is configured
   * for `profile_class = "Location"`. Renders a banner instead of the
   * form and short-circuits the auto-fire on `/emplacement/:slug`.
   */
  agentNotConfigured?: boolean;
}

// Single-page React view served at /outils/analyse-de-local AND at
// /outils/analyse-de-local/emplacement/:slug.
//
// Request lifecycle (async via ActionCable):
//   * Cache hit  → POST returns 200 with the full analysis. Render
//                  immediately.
//   * Cache miss → POST returns 202 + { job_id }. Open ActionCable
//                  subscription to LocationAnalysis::AnalysisChannel
//                  keyed by job_id. Keep `loading=true` (the form
//                  shows "Analyse en cours…"). When the job
//                  broadcasts `analysis_completed`, render. On
//                  `analysis_failed`, surface the error.
//
// Quota model:
//   The page never blocks on landing. The form is always shown.
//   QuotaExhausted upsell appears only after a 403 from the server
//   (cache miss + visitor already gated). Cache hits are free.
export function AnalysePage({
  preselectedLocation,
  preselectError,
  agentNotConfigured = false,
}: AnalysePageProps) {
  const [selected, setSelected] = useState<LocationSuggestion | null>(
    preselectedLocation ?? null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(preselectError ?? null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [analyzedLocation, setAnalyzedLocation] = useState<AnalyzedLocation | null>(null);
  const [gated, setGated] = useState(false);

  // ActionCable plumbing — kept in refs so cleanup is reliable and
  // re-renders never recreate the consumer/subscription.
  const consumerRef = useRef<Consumer | null>(null);
  // `@rails/actioncable`'s `Subscription` is generic over a per-channel
  // mixins type. We don't add custom mixins; the empty object satisfies
  // the constraint and we only ever call `unsubscribe()` on it.
  const subscriptionRef = useRef<Subscription<object> | null>(null);

  const cleanupCable = useCallback(() => {
    subscriptionRef.current?.unsubscribe();
    consumerRef.current?.disconnect();
    subscriptionRef.current = null;
    consumerRef.current = null;
  }, []);

  // Open an ActionCable subscription for a freshly-enqueued job and
  // wire its broadcasts to component state. Returns nothing — all
  // state transitions happen via the `received` callback.
  const subscribeToJob = useCallback(
    (jobId: string) => {
      cleanupCable();
      const consumer = createConsumer();
      consumerRef.current = consumer;

      subscriptionRef.current = consumer.subscriptions.create(
        { channel: "LocationAnalysis::AnalysisChannel", job_id: jobId },
        {
          received: (data: AnalysisCableMessage) => {
            if (data.type === "analysis_completed") {
              setResult(data.analysis);
              setAnalyzedLocation(data.location);
              setLoading(false);
              cleanupCable();
            } else if (data.type === "analysis_failed") {
              setError(data.error || "L'analyse a échoué. Réessayez dans quelques instants.");
              setLoading(false);
              cleanupCable();
            }
          },
        },
      );
    },
    [cleanupCable],
  );

  // Cleanup on unmount — drop the subscription so we don't leak.
  useEffect(() => cleanupCable, [cleanupCable]);

  const submit = async (locationId?: string | number) => {
    if (agentNotConfigured) return;
    const id = locationId ?? selected?.id;
    if (!id) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setAnalyzedLocation(null);
    cleanupCable();
    try {
      const outcome = await analyseLocation(id);
      if (outcome.kind === "completed") {
        // Cache hit fast path — server already had the analysis in
        // the Location's commercial_analysis column. No LLM call ran,
        // no quota was consumed.
        setResult(outcome.response.analysis);
        setAnalyzedLocation(outcome.response.location);
        if (outcome.response.gated) setGated(true);
        setLoading(false);
      } else {
        // Cache miss → an AnalysisJob is running on the backend.
        // Keep `loading=true` and wait for the cable broadcast. The
        // server already consumed the visitor's quota on enqueue, so
        // a returning visitor on the same Location after this run
        // will hit the cache and be free.
        setAnalyzedLocation(outcome.location);
        subscribeToJob(outcome.jobId);
      }
    } catch (err) {
      // 403 from the backend → cache miss + quota exhausted. Flip to
      // the upsell. We use `err?.response?.status` (no axios.isAxiosError
      // guard) so the path is robust against bundler / version edge
      // cases that previously made AxiosError detection unreliable.
      if ((err as { response?: { status?: number } })?.response?.status === 403) {
        setGated(true);
        setLoading(false);
        return;
      }

      const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
      setError(data?.error || "L'analyse a échoué. Réessayez dans quelques instants.");
      setLoading(false);
    }
  };

  // Auto-fire on mount when the visitor lands on /emplacement/:slug
  // with a server-resolved Location AND the server didn't hand us a
  // soft error AND the agent IS configured.
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (agentNotConfigured) return;
    if (!preselectedLocation || preselectError) return;
    autoFiredRef.current = true;
    submit(preselectedLocation.id);
    // submit closure captures `selected` (null at this point) but we
    // pass the id explicitly so the closure value doesn't matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    cleanupCable();
    setResult(null);
    setError(null);
    setSelected(null);
    setAnalyzedLocation(null);
  };

  if (agentNotConfigured) {
    return (
      <div className="w-full px-4 pb-12 sm:mx-auto sm:max-w-xl">
        <div
          role="alert"
          data-testid="agent-not-configured"
          className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <p className="font-semibold mb-1">
            L'outil d'analyse n'est pas encore configuré.
          </p>
          <p>
            Un administrateur doit publier l'agent d'analyse commerciale
            avant que cet outil ne soit disponible. Réessayez dans
            quelques instants.
          </p>
        </div>
      </div>
    );
  }

  if (gated && !result) {
    return <QuotaExhausted />;
  }

  return (
    <div className="w-full px-4 pb-12 sm:mx-auto sm:max-w-xl">
      {!result && (
        <AnalysisForm
          selected={selected}
          onSelect={setSelected}
          onSubmit={() => submit()}
          loading={loading}
          error={error}
        />
      )}

      {result && (
        <AnalysisResultView
          result={result}
          location={analyzedLocation ?? undefined}
          onReset={reset}
        />
      )}
    </div>
  );
}
